import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { Text } from '../../..'
import {
  getLinkToDisclaimersPage,
  useComplianceData,
} from '../../../../services/mlsComplianceDataService'
import { ListingDetailsListing } from '../../../../types/listings'
import { ReactNode } from 'react'
import styles from './ListingMetadata.module.scss'
import { Link } from 'react-router-dom'
import { format, formatDistance, parseISO } from 'date-fns'
import { MlsLogo } from '../MlsLogo'

export type ListingDataRequiredByListingsMetadata = Pick<
  ListingDetailsListing,
  | 'source'
  | 'originatingSystemName'
  | 'disclaimer'
  | 'mlsNumber'
  | 'updatedAt'
  | 'lastCheckedAt'
  | 'providedBy'
>

interface ListingMetadataProps {
  listing: ListingDataRequiredByListingsMetadata
}

interface CellTitleTextProps {
  children: ReactNode
}

export const ListingMetadata = ({ listing }: ListingMetadataProps) => {
  const complianceData = useComplianceData(listing)
  const intl = useIntl()

  const translations = defineMessages({
    logoLinkAriaLabel: {
      id: 'HomeSearch.listingDetails.listingMetadata.logoLinkAriaLabel',
      defaultMessage: 'Read full copyright and disclaimer for this MLS',
    },
  })

  const HeaderText = () => (
    <Text
      data-qa='listingProvidedByText'
      element='span'
      size='l'
      variant='neutralCool'
      variantCount='900'
      weight='bold'
    >
      <FormattedMessage
        id='HomeSearch.listingDetails.listingMetadata.header'
        defaultMessage='Listing metadata'
      />
    </Text>
  )

  const CellTitleText = ({ children }: CellTitleTextProps) => (
    <Text element='span' variant='neutralCool' variantCount='700'>
      {children}
    </Text>
  )

  const CellValueText = ({ children }: CellTitleTextProps) => (
    <Text
      element='span'
      variant='neutralCool'
      variantCount='700'
      weight='semibold'
    >
      {children}
    </Text>
  )

  if (!listing) {
    return null
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <HeaderText />
      </div>

      <div>
        <div className={styles.gridRow}>
          {listing.updatedAt && (
            <div className={styles.gridCell}>
              <div>
                <CellTitleText>
                  <FormattedMessage
                    id='HomeSearch.listingDetails.listingMetadata.lastUpdated'
                    defaultMessage='Last Updated'
                  />
                </CellTitleText>
              </div>
              <div>
                <CellValueText>
                  {format(parseISO(listing.updatedAt), 'eee, MMM d y')}
                </CellValueText>
              </div>
            </div>
          )}

          {listing.lastCheckedAt && (
            <div className={styles.gridCell}>
              <div>
                <CellTitleText>
                  <FormattedMessage
                    id='HomeSearch.listingDetails.listingMetadata.lastChecked'
                    defaultMessage='Last Checked'
                  />
                </CellTitleText>
              </div>
              <div>
                <CellValueText>
                  {formatDistance(parseISO(listing.lastCheckedAt), new Date(), {
                    addSuffix: true,
                  })}
                </CellValueText>
              </div>
            </div>
          )}
        </div>

        <div className={styles.gridRow}>
          {complianceData.mlsName && (
            <div className={styles.gridCell}>
              <div>
                <CellTitleText>
                  {listing?.source?.name === 'EveryState' ? (
                    <FormattedMessage
                      id='HomeSearch.listingDetails.listingMetadata.listingProvider'
                      defaultMessage='Listing Provider'
                    />
                  ) : (
                    <FormattedMessage
                      id='HomeSearch.listingDetails.listingMetadata.listhub.source'
                      defaultMessage='Source'
                    />
                  )}
                </CellTitleText>
              </div>
              <div>
                <CellValueText>
                  {listing?.source?.name === 'ListHub' ? (
                    <Link
                      to={{ pathname: listing.source.link }}
                      target='_blank'
                    >
                      {complianceData.mlsName}
                    </Link>
                  ) : (
                    <Link
                      to={getLinkToDisclaimersPage(
                        listing.originatingSystemName
                      )}
                      target='_blank'
                    >
                      {complianceData.mlsName}
                    </Link>
                  )}
                </CellValueText>
              </div>
            </div>
          )}

          {listing?.mlsNumber && (
            <div className={styles.gridCell}>
              <div>
                <CellTitleText>
                  <FormattedMessage
                    id='HomeSearch.listingDetails.listingMetadata.mlsNumber'
                    defaultMessage='MLS Number'
                  />
                </CellTitleText>
              </div>
              <div>
                <CellValueText>{listing?.mlsNumber}</CellValueText>
              </div>
            </div>
          )}
        </div>
      </div>

      {listing?.source?.name === 'ListHub' && (
        <div className={styles.listhubProvidedBy}>
          <Text element='div' variant='neutralCool' variantCount='700'>
            <FormattedMessage
              id='HomeSearch.listingDetails.listingMetadata.listhub.listingProvidedBy'
              defaultMessage='Listing provider'
            />
          </Text>
          <Text
            element='div'
            variant='neutralCool'
            variantCount='700'
            weight='semibold'
          >
            {listing.providedBy}
          </Text>
        </div>
      )}
      <Text element='div' variant='neutralCool' variantCount='700'>
        <FormattedMessage
          id='HomeSearch.listingDetails.listingMetadata.disclaimerTitle'
          defaultMessage='Disclaimer'
        />
      </Text>
      <Text
        element='div'
        variant='neutralCool'
        variantCount='700'
        weight='semibold'
        className={styles.disclaimer}
      >
        {complianceData.disclaimer}
      </Text>
      {listing?.source?.name === 'EveryState' && (
        <>
          <div className={styles.fullDisclaimer}>
            <Link
              to={getLinkToDisclaimersPage(listing.originatingSystemName)}
              target='_blank'
            >
              <FormattedMessage
                id='HomeSearch.listingDetails.listingMetadata.fullDisclaimerLinkText'
                defaultMessage='Read full copyright and disclaimer'
              />
            </Link>
          </div>
          <Link
            aria-label={intl.formatMessage(translations.logoLinkAriaLabel)}
            to={getLinkToDisclaimersPage(listing.originatingSystemName)}
            target='_blank'
          >
            <MlsLogo listing={listing} />
          </Link>
        </>
      )}
    </div>
  )
}
